import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import FormGroup from "./form/FormGroup";
import api from "./api/api";
import { useAppStore } from "../store/useAppStore";

// Etichetta "Nome (IP)" per le tendine dei dispositivi, con l'IP impostato da backoffice
const withIp = (devices, ipField = "ip") =>
    (devices || []).map((d) => ({ ...d, name: d[ipField] ? `${d.name} (${d[ipField]})` : d.name }));

function SettingsToggleField({ title, description, value, onChange }) {
    const isYes = value === 1 || value === "1" || value === true;

    return (
        <div className={"form-group settings-boolean-field" + (isYes ? " is-yes" : " is-no")}>
            <div className="settings-boolean-copy">
                <div className="settings-boolean-title">{title}</div>
                <div className="settings-boolean-description">{description}</div>
            </div>
            <button
                type="button"
                className={"settings-yesno-toggle" + (isYes ? " is-yes" : " is-no")}
                onClick={() => onChange(isYes ? 0 : 1)}
                aria-pressed={isYes}
            >
                <span className="settings-yesno-toggle-knob" aria-hidden="true"></span>
                <span className="settings-yesno-toggle-text">{isYes ? "SI" : "NO"}</span>
            </button>
        </div>
    );
}

// Terminale Dojo: elenco dei terminali disponibili del negozio oppure ID inserito a mano
function DojoSettings({ shop, config, setConfig }) {
    const [terminals, setTerminals] = useState([]);
    const [terminalsError, setTerminalsError] = useState(null);
    const [terminalsLoading, setTerminalsLoading] = useState(false);

    const loadTerminals = async () => {
        if (!shop?.id) return;
        setTerminalsLoading(true);
        setTerminalsError(null);
        const res = await api.postProtected("/dojo/terminals/", { shop_id: shop.id });
        setTerminalsLoading(false);
        if (res.success == 1) setTerminals(res.rows || []);
        else {
            setTerminals([]);
            setTerminalsError(res.error || "Impossibile caricare i terminali Dojo");
        }
    };

    useEffect(() => {
        loadTerminals();
    }, [shop?.id]);

    const terminalId = config.dojo_terminal_id || "";

    return (
        <div className="settings-content settings-content-two-cols">
            <h2>Dojo</h2>
            <div className="settings-fields-grid single">
                <div className="form-group">
                    <div className="label">Terminale Dojo:</div>
                    <select
                        name="dojo_terminal_select"
                        value={terminals.find((t) => t.id == terminalId) ? terminalId : ""}
                        onChange={(e) => setConfig({ ...config, dojo_terminal_id: e.target.value })}
                    >
                        <option value="">{terminalsLoading ? "Caricamento..." : ""}</option>
                        {terminals.map((t) => (
                            <option key={t.id} value={t.id}>
                                {(t.properties?.tid || t.id) + " (" + t.status + ")"}
                            </option>
                        ))}
                    </select>
                    {terminalsError && <div className="description">Errore: {terminalsError}</div>}
                    <button type="button" onClick={loadTerminals}>
                        Aggiorna elenco
                    </button>
                </div>
                <div className="form-group">
                    <div className="label">Terminal ID (inserimento manuale):</div>
                    <input
                        type="text"
                        name="dojo_terminal_id"
                        value={terminalId}
                        onChange={(e) => setConfig({ ...config, dojo_terminal_id: e.target.value.trim() })}
                    />
                </div>
            </div>
        </div>
    );
}

function Settings() {
    const {
        apiSettings,
        setSettingsVisible,
        settingsPage,
        setSettingsPage,
        config,
        setConfig,
        fiscalPrinters,
        thermalPrinters,
        filteredCategories,
        shop,
        glories,
        cashmatics,
        posP17Terminals,
        loadColors,
        setLoading,
        setConfirm,
    } = useContext(AppContext);
    const { virtualPrinters } = useAppStore();
    const hasDojo = !!shop?.payment_methods?.find((m) => m.id == 23);

    const saveColor = async (request) => {
        if (!request?.shop_id || !request?.product_id) return;

        setLoading(true);
        try {
            const response = await api.postProtected("/pos2/save_color/", request);
            if (response?.success == 0) {
                setConfirm({
                    text: response.error || "Errore nel salvataggio del colore prodotto.",
                    confirmButton: "Ok",
                });
            } else if (typeof loadColors === "function") {
                await loadColors();
            }
        } catch (error) {
            setConfirm({
                text: "Errore nel salvataggio del colore prodotto.",
                confirmButton: "Ok",
            });
            console.error("saveColor error", error);
        } finally {
            setLoading(false);
        }
    };

    const mainSettingsFields = [
        {
            key: "confirm_order",
            title: "Conferma ordine",
            description: "Richiede conferma prima del salvataggio dell'ordine.",
        },
        {
            key: "keep_open_after_checkout",
            title: "Mantieni ordine aperto",
            description: "Dopo invio/salvataggio l'ordine rimane aperto sul tavolo.",
        },
        {
            key: "skip_payment_request_for_payed",
            title: "Salta richiesta pagamento",
            description: "Non chiede metodo pagamento per ordini già pagati.",
        },
        {
            key: "optional_print",
            title: "Stampa comanda opzionale",
            description: "Permette di non stampare la comanda in fase di invio.",
        },
        {
            key: "cash_drawer_closures",
            title: "Apertura/chiusura turni",
            description: "Abilita gestione turni operatore (apri/chiudi turno).",
        },
        {
            key: "rest",
            title: "Calcolo resto contanti",
            description: "Attiva il calcolo automatico del resto sui pagamenti cash.",
        },
        {
            key: "time_selection_v2",
            title: "Scelta orario v2",
            description: "Usa la nuova modalità di selezione orario.",
        },
        {
            key: "orders_list_v2",
            title: "Abilita lista ordini v2",
            description: "Usa la nuova versione dell'endpoint per il caricamento degli ordini.",
        },
        {
            key: "reset_delivery_type_to_shop",
            title: "Reset tipo ordine a Banco",
            description: "Ad ogni nuovo ordine ripristina il tipo di consegna a Banco (Sala).",
        },
    ];

    return (
        <div className="settings">
            <div className="settings-container">
                <div className="settings-header">
                    <h2>Impostazioni</h2>
                    <button onClick={() => setSettingsVisible(false)}>&times;</button>
                </div>
                <div className="settings-columns">
                    <div className="settings-menu">
                        <button
                            className={settingsPage == "main" ? "selected" : ""}
                            onClick={() => setSettingsPage("main")}
                        >
                            Generali
                        </button>
                        <button
                            className={settingsPage == "visual" ? "selected" : ""}
                            onClick={() => setSettingsPage("visual")}
                        >
                            Visualizzazione
                        </button>
                        <button
                            className={settingsPage == "products" ? "selected" : ""}
                            onClick={() => setSettingsPage("products")}
                        >
                            Prodotti
                        </button>
                        <button
                            className={settingsPage == "thermal_printer" ? "selected" : ""}
                            onClick={() => setSettingsPage("thermal_printer")}
                        >
                            Stampante Preconto
                        </button>
                        {apiSettings?.mandant?.fiscal_printers == 1 && (
                            <button
                                className={settingsPage == "fiscal_printer" ? "selected" : ""}
                                onClick={() => setSettingsPage("fiscal_printer")}
                            >
                                Stampante Fiscale
                            </button>
                        )}
                        {apiSettings?.mandant?.fiskaly == 1 && (
                            <button
                                className={settingsPage == "virtual_printer" ? "selected" : ""}
                                onClick={() => setSettingsPage("virtual_printer")}
                            >
                                Stampante Virtuale
                            </button>
                        )}
                        {apiSettings?.mandant?.glory == 1 && (
                            <button
                                className={settingsPage == "glory" ? "selected" : ""}
                                onClick={() => setSettingsPage("glory")}
                            >
                                Glory Cash Drawer
                            </button>
                        )}
                        {apiSettings?.mandant?.cashmatic == 1 && (
                            <button
                                className={settingsPage == "cashmatic" ? "selected" : ""}
                                onClick={() => setSettingsPage("cashmatic")}
                            >
                                Cashmatic
                            </button>
                        )}
                        <button
                            className={settingsPage == "vivawallet" ? "selected" : ""}
                            onClick={() => setSettingsPage("vivawallet")}
                        >
                            Viva Wallet Terminal
                        </button>
                        {hasDojo && (
                            <button
                                className={settingsPage == "dojo" ? "selected" : ""}
                                onClick={() => setSettingsPage("dojo")}
                            >
                                Dojo
                            </button>
                        )}
                        {apiSettings?.mandant?.pos_p17 == 1 && (
                            <button
                                className={settingsPage == "pos_p17" ? "selected" : ""}
                                onClick={() => setSettingsPage("pos_p17")}
                            >
                                POS Protocollo 17
                            </button>
                        )}
                        <button
                            className={settingsPage == "quick_payment" ? "selected" : ""}
                            onClick={() => setSettingsPage("quick_payment")}
                        >
                            Pagamento Rapido
                        </button>
                    </div>
                    {settingsPage == "main" && (
                        <div className="settings-content settings-content-main">
                            <h2>Generali</h2>
                            <div className="settings-main-grid">
                                {mainSettingsFields.map((field) => (
                                    <SettingsToggleField
                                        key={field.key}
                                        title={field.title}
                                        description={field.description}
                                        value={config[field.key] ?? 0}
                                        onChange={(value) =>
                                            setConfig({
                                                ...config,
                                                [field.key]: value,
                                            })
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {settingsPage == "visual" && (
                        <div className="settings-content settings-content-main">
                            <h2>Visualizzazione</h2>
                            <div className="settings-main-grid">
                                <div className="form-group settings-boolean-field settings-visual-select">
                                    <div className="settings-boolean-copy">
                                        <div className="settings-boolean-title">
                                            Altezza pulsante prodotto
                                        </div>
                                        <div className="settings-boolean-description">
                                            Definisce se il box prodotto mantiene altezza fissa o automatica.
                                        </div>
                                    </div>
                                    <select
                                        name="product_button_height"
                                        value={config.product_button_height}
                                        onChange={(e) =>
                                            setConfig({
                                                ...config,
                                                product_button_height: e.target.value,
                                            })
                                        }
                                    >
                                        <option value="fixed">Fissa</option>
                                        <option value="auto">Auto</option>
                                    </select>
                                </div>
                                <SettingsToggleField
                                    title="Immagine prodotto"
                                    description="Attiva visualizzazione immagine prodotto."
                                    value={config.show_product_image ?? 0}
                                    onChange={(value) =>
                                        setConfig({
                                            ...config,
                                            show_product_image: value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    )}
                    {settingsPage == "products" && (
                        <div className="settings-content">
                            <h2>Prodotti</h2>
                            <div className="products-color">
                                {filteredCategories.map((course, i) => (
                                    <div key={i}>
                                        <h3>{course.name || "Altro"}</h3>
                                        {course.categories.map((category, n) => (
                                            <div key={n}>
                                                <h4>{category.name}</h4>
                                                {category.products.map((product, y) => (
                                                    <div key={y}>
                                                        <div className="product">
                                                            {product.name}
                                                            <div className="buttons">
                                                                {[
                                                                    "#f94144",
                                                                    "#d264b6",
                                                                    "#f8961e",
                                                                    "#f4acb7",
                                                                    "#f9c74f",
                                                                    "#90be6d",
                                                                    "#43aa8b",
                                                                    "#4d908e",
                                                                    "#577590",
                                                                    "#277da1",
                                                                ].map((color, i) => (
                                                                    <button
                                                                        key={i}
                                                                        style={{
                                                                            backgroundColor: color,
                                                                        }}
                                                                        onClick={() =>
                                                                            saveColor({
                                                                                shop_id: shop.id,
                                                                                product_id: product.id,
                                                                                color: color,
                                                                            })
                                                                        }
                                                                    >
                                                                        {product.color == color && (
                                                                            <>&#x2714;</>
                                                                        )}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {settingsPage == "fiscal_printer" && (
                        <div className="settings-content settings-content-two-cols">
                            <h2>Stampante Fiscale</h2>
                            <div className="settings-fields-grid">
                                <FormGroup
                                    label="Stampante"
                                    name="fiscal_printer_id"
                                    type="select"
                                    value={config.fiscal_printer_id}
                                    onChange={(e) =>
                                        setConfig({ ...config, fiscal_printer_id: e.target.value })
                                    }
                                    options={[{ id: null, name: "" }, ...withIp(fiscalPrinters)]}
                                />
                                <SettingsToggleField
                                    title="Controllo chiusura giornaliera"
                                    description="Mostra il controllo e richiede la chiusura giornaliera."
                                    value={config.check_fiscal_closure ?? 0}
                                    onChange={(value) =>
                                        setConfig({
                                            ...config,
                                            check_fiscal_closure: value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    )}
                    {settingsPage == "thermal_printer" && (
                        <div className="settings-content settings-content-two-cols">
                            <h2>Stampante Preconto</h2>
                            <div className="settings-fields-grid">
                                <FormGroup
                                    label="Seleziona stampante per stampa preconto"
                                    name="thermal_printer_id"
                                    type="select"
                                    value={config.thermal_printer_id}
                                    onChange={(e) =>
                                        setConfig({ ...config, thermal_printer_id: e.target.value })
                                    }
                                    options={[{ id: null, name: "" }, ...withIp(thermalPrinters)]}
                                />
                                <SettingsToggleField
                                    title="Stampa preconto in chiusura"
                                    description="Esegue stampa preconto sulla stampante selezionata."
                                    value={config.prebill_on_checkout ?? 0}
                                    onChange={(value) =>
                                        setConfig({
                                            ...config,
                                            prebill_on_checkout: value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    )}
                    {settingsPage == "virtual_printer" && (
                        <div className="settings-content settings-content-two-cols">
                            <h2>Stampante Virtuale</h2>
                            <div className="settings-fields-grid single">
                                <FormGroup
                                    label="Stampante"
                                    name="virtual_printer_id"
                                    type="select"
                                    value={config.virtual_printer_id}
                                    onChange={(e) =>
                                        setConfig({ ...config, virtual_printer_id: e.target.value })
                                    }
                                    options={[{ id: null, name: "" }, ...virtualPrinters]}
                                />
                            </div>
                        </div>
                    )}
                    {settingsPage == "glory" && (
                        <div className="settings-content">
                            <h2>Glory Cash Drawer</h2>
                            <FormGroup
                                label="Macchina Glory"
                                name="glory_id"
                                type="select"
                                value={config.glory_id}
                                onChange={(e) => setConfig({ ...config, glory_id: e.target.value })}
                                options={[{ id: null, name: "" }, ...withIp(glories, "fcc_ip")]}
                            />
                        </div>
                    )}
                    {settingsPage == "cashmatic" && (
                        <div className="settings-content">
                            <h2>Cashmatic</h2>
                            <FormGroup
                                label="Macchina Cashmatic"
                                name="cashmatic_id"
                                type="select"
                                value={config.cashmatic_id}
                                onChange={(e) => setConfig({ ...config, cashmatic_id: e.target.value })}
                                options={[{ id: null, name: "" }, ...withIp(cashmatics)]}
                            />
                        </div>
                    )}
                    {settingsPage == "vivawallet" && (
                        <div className="settings-content settings-content-two-cols">
                            <h2>Viva Wallet Terminal</h2>
                            <div className="settings-fields-grid single">
                                <FormGroup
                                    label="Terminal ID"
                                    name="viva_terminal_id"
                                    type="text"
                                    value={config.viva_terminal_id}
                                    onChange={(e) =>
                                        setConfig({ ...config, viva_terminal_id: e.target.value })
                                    }
                                />
                            </div>
                        </div>
                    )}
                    {settingsPage == "dojo" && hasDojo && (
                        <DojoSettings shop={shop} config={config} setConfig={setConfig} />
                    )}
                    {settingsPage == "pos_p17" && (
                        <div className="settings-content">
                            <h2>POS Protocollo 17</h2>
                            <FormGroup
                                label="Terminale POS"
                                name="pos_p17_id"
                                type="select"
                                value={config.pos_p17_id}
                                onChange={(e) => setConfig({ ...config, pos_p17_id: e.target.value })}
                                options={[{ id: null, name: "" }, ...withIp(posP17Terminals)]}
                            />
                        </div>
                    )}
                    {settingsPage == "quick_payment" && (
                        <div className="settings-content settings-content-two-cols">
                            <h2>Pagamento Rapido</h2>
                            <div className="settings-fields-grid">
                                <SettingsToggleField
                                    title="Vista pagamento rapido"
                                    description="Mostra nel footer del carrello una sezione con i metodi di pagamento preferiti per incassare l'intero importo in un click."
                                    value={config.quick_payment_footer ?? 0}
                                    onChange={(value) =>
                                        setConfig({
                                            ...config,
                                            quick_payment_footer: value,
                                        })
                                    }
                                />
                                {[0, 1, 2].map((i) => (
                                    <FormGroup
                                        key={i}
                                        label={"Metodo pagamento rapido " + (i + 1)}
                                        name={"quick_payment_method_id_" + i}
                                        type="select"
                                        value={config.quick_payment_method_ids?.[i] ?? null}
                                        onChange={(e) =>
                                            setConfig({
                                                ...config,
                                                quick_payment_method_ids: [0, 1, 2].map((idx) =>
                                                    idx === i
                                                        ? e.target.value
                                                        : (config.quick_payment_method_ids?.[idx] ?? null),
                                                ),
                                            })
                                        }
                                        options={[{ id: null, name: "" }, ...(shop?.payment_methods || [])]}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <button className="settings-save-floating" onClick={() => setSettingsVisible(false)}>
                    SALVA
                </button>
            </div>
        </div>
    );
}

export default Settings;
